import React, { useEffect, useState } from "react";
import {
  Avatar,
  MessageOptions,
  ReactionsList,
  Attachment,
  useMessageContext,
  useChannelStateContext,
  useChatContext,
} from "stream-chat-react";
import "./CustomMessage.css";
import { linkify } from "../../utils/linkify";

const COLOR_PALETTE = [
  "#00b894", // Teal
  "#7700FF", // Purple
  "#FF6B6B", // Red
  "#4ECDC4", // Turquoise
  "#45B7D1", // Blue
  "#FFA07A", // Light Salmon
  "#98D8C8", // Mint
  "#F7DC6F", // Yellow
  "#BB8FCE", // Lavender
  "#85C1E2", // Sky Blue
  "#F8B739", // Orange
  "#52BE80", // Green
  "#EB984E", // Peach
  "#AF7AC5", // Plum
  "#76D7C4", // Aqua
  "#F1C40F", // Gold
  "#E74C3C", // Dark Red
  "#3498DB", // Royal Blue
  "#16A085", // Dark Teal
  "#D35400", // Dark Orange
];

function generateColorFromName(name) {
  if (!name) return COLOR_PALETTE[0];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const index = Math.abs(hash % COLOR_PALETTE.length);
  return COLOR_PALETTE[index];
}

const CustomMessage = () => {
  const { message, isMyMessage, editing, clearEditingState } =
    useMessageContext();
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();

  const mine = isMyMessage();
  const isDeleted = Boolean(message?.deleted_at) || message?.type === "deleted";
  const isEdited = Boolean(message?.message_text_updated_at);
  const myUserId = client?.userID || client?.user?.id;

  const avatarColor = generateColorFromName(
    message.user?.name || message.user?.id
  );

  const [, bump] = useState(0);
  const [mutedUserIds, setMutedUserIds] = useState(new Set());
  const getGlobalMutedSet = () => {
    try {
      if (typeof window === "undefined") return null;
      if (!window.__streamMutedUserIds) {
        window.__streamMutedUserIds = new Set();
      }
      return window.__streamMutedUserIds;
    } catch (e) {
      return null;
    }
  };
  const writeGlobalMutedSet = (setLike) => {
    try {
      const g = getGlobalMutedSet();
      if (!g) return;
      g.clear();
      for (const id of setLike) g.add(id);
    } catch (e) {}
  };
  useEffect(() => {
    if (!client) return;
    const collectFromClient = () => {
      try {
        const mutes = Array.isArray(client?.user?.mutes)
          ? client.user.mutes
          : [];
        const ids = new Set(
          mutes
            .map((m) => (typeof m === "string" ? m : m?.target?.id || m?.id))
            .filter(Boolean)
        );
        setMutedUserIds(ids);
        writeGlobalMutedSet(ids);
        bump((v) => v + 1);
      } catch (e) {}
    };
    const onUserMuted = (e) => {
      try {
        const targetId =
          e?.target?.id ||
          e?.target_user?.id ||
          e?.mute?.target?.id ||
          e?.user_mute?.target?.id;
        if (targetId) {
          try {
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("chat-mute-changed", {
                  detail: { userId: targetId, muted: true },
                })
              );
            }
          } catch (e3) {}
          setMutedUserIds((prev) => {
            const next = new Set(prev);
            next.add(targetId);
            writeGlobalMutedSet(next);
            return next;
          });
          bump((v) => v + 1);
          return;
        }
      } catch (e2) {}
      collectFromClient();
    };
    const onUserUnmuted = (e) => {
      try {
        const targetId =
          e?.target?.id ||
          e?.target_user?.id ||
          e?.mute?.target?.id ||
          e?.user_mute?.target?.id;
        if (targetId) {
          try {
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("chat-mute-changed", {
                  detail: { userId: targetId, muted: false },
                })
              );
            }
          } catch (e3) {}
          setMutedUserIds((prev) => {
            const next = new Set(prev);
            next.delete(targetId);
            writeGlobalMutedSet(next);
            return next;
          });
          bump((v) => v + 1);
          return;
        }
      } catch (e2) {}
      collectFromClient();
    };

    const g = getGlobalMutedSet();
    if (g && g.size > 0) {
      setMutedUserIds(new Set(g));
      bump((v) => v + 1);
    } else {
      collectFromClient();
    }
    // Subscribe to updates
    const off1 = client.on("notification.mutes_updated", collectFromClient);
    const off2 = client.on("user.muted", onUserMuted);
    const off3 = client.on("user.unmuted", onUserUnmuted);
    return () => {
      try {
        off1?.();
        off2?.();
        off3?.();
      } catch (e) {}
    };
  }, [client]);


  useEffect(() => {
    const onChanged = (e) => {
      try {
        const userId = e?.detail?.userId;
        const muted = !!e?.detail?.muted;
        if (!userId) return;
        setMutedUserIds((prev) => {
          const next = new Set(prev);
          if (muted) next.add(userId);
          else next.delete(userId);
          writeGlobalMutedSet(next);
          return next;
        });
        bump((v) => v + 1);
      } catch (e2) {}
    };
    try {
      if (typeof window !== "undefined") {
        window.addEventListener("chat-mute-changed", onChanged);
      }
    } catch (e) {}
    return () => {
      try {
        if (typeof window !== "undefined") {
          window.removeEventListener("chat-mute-changed", onChanged);
        }
      } catch (e) {}
    };
  }, []);

  const isMutedByMe = mutedUserIds.has(message?.user?.id);

  useEffect(() => {
    const onMarked = (e) => {
      try {
        const cid = e?.detail?.cid;
        if (!cid || cid !== channel?.cid) return;
        const elements = document.getElementsByClassName(
          "str-chat__textarea__textarea"
        );
        if (elements && elements.length) {
        }
      } catch (e2) {}
    };
    try {
      if (typeof window !== "undefined") {
        window.addEventListener("chat-mark-read", onMarked);
      }
    } catch (e) {}
    return () => {
      try {
        if (typeof window !== "undefined") {
          window.removeEventListener("chat-mark-read", onMarked);
        }
      } catch (e) {}
    };
  }, [channel?.cid]);

  const handleInlineReplyAsQuote = () => {
    try {
      if (!message) return;
      try {
        channel?.messageComposer?.restore?.();
      } catch (e) {}
      channel?.messageComposer?.setQuotedMessage?.(message);
      const elements = document.getElementsByClassName(
        "str-chat__textarea__textarea"
      );
      const textarea = elements.item(0);
      if (textarea instanceof HTMLTextAreaElement) {
        textarea.focus();
      }
    } catch (e) {}
  };

  useEffect(() => {
    if (!editing) return;
    try {
      if (!channel?.messageComposer || !message) return;
      try {
        channel.messageComposer.restore?.();
      } catch (e) {}
      channel.messageComposer.editedMessage = message;
      channel.messageComposer.initState({ composition: message });
      const elements = document.getElementsByClassName(
        "str-chat__textarea__textarea"
      );
      const textarea = elements.item(0);
      if (textarea instanceof HTMLTextAreaElement) {
        textarea.focus();
      }
    } catch (e) {}
  }, [editing, channel, message?.id, clearEditingState]);

  if (isMutedByMe && !mine) {
    return (
      <div className={`custom-message theirs`} style={{ opacity: 0.6 }}>
        <div className="msg-row">
          <div style={{ "--avatar-color": avatarColor }}>
            <Avatar
              image={message.user?.image}
              name={message.user?.name || message.user?.id}
              size={36}
              data-user={message.user?.id}
              className="custom-avatar"
            />
          </div>
          <div className="msg-bubble">
            <div className="msg-header">
              <span className="msg-sender">
                {message.user?.name || "Unknown"}
              </span>
              <span className="msg-time">
                {new Date(message.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <div className="msg-deleted" style={{ padding: "8px 12px" }}>
              <span style={{ fontSize: "13px", color: "#9ca3af" }}>
                🔇 Messages from this user are muted
              </span>
              <button
                onClick={async (e) => {
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  try {
                    const uid = message?.user?.id;
                    await client?.unmuteUser?.(uid);
                    setMutedUserIds((prev) => {
                      const next = new Set(prev);
                      next.delete(uid);
                      writeGlobalMutedSet(next);
                      return next;
                    });
                    try {
                      if (typeof window !== "undefined") {
                        window.dispatchEvent(
                          new CustomEvent("chat-mute-changed", {
                            detail: { userId: uid, muted: false },
                          })
                        );
                      }
                    } catch (e3) {}
                  } catch (e) {
                    console.error("Failed to unmute user", e);
                  }
                }}
                style={{
                  marginInlineStart: 8,
                  fontSize: 12,
                  padding: "4px 12px",
                  borderRadius: 6,
                  border: "1px solid rgba(37, 99, 235, 0.5)",
                  background: "rgba(37, 99, 235, 0.1)",
                  color: "#60a5fa",
                  cursor: "pointer",
                  fontWeight: 500,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(37, 99, 235, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(37, 99, 235, 0.7)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(37, 99, 235, 0.1)";
                  e.currentTarget.style.borderColor = "rgba(37, 99, 235, 0.5)";
                }}
              >
                Unmute
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`custom-message ${mine ? "mine" : "theirs"}`}>
      <div className="msg-row">
        {!isMutedByMe && mine && (
          <div style={{ "--avatar-color": avatarColor }}>
            <Avatar
              image={message.user?.image}
              name={message.user?.name || message.user?.id}
              size={36}
              data-user={message.user?.id}
              className="custom-avatar"
            />
          </div>
        )}

        <div className="msg-bubble">
          <div className="msg-header">
            <span className="msg-sender">
              {message.user?.name || "Unknown"}
            </span>
            <span className="msg-time">
              {new Date(message.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            {/* Unread badge removed */}
          </div>

          {isDeleted ? (
            <div className="msg-deleted">This message was deleted.</div>
          ) : (
            <>
              {message.quoted_message && (
                <div className="msg-quote">
                  <div className="msg-quote-author">
                    {message.quoted_message.user?.name || "Unknown"}
                  </div>
                  {message.quoted_message.text ? (
                    <div className="msg-quote-text">
                      {linkify(message.quoted_message.text)}
                    </div>
                  ) : null}
                </div>
              )}

              <p>
                {linkify(message.text)}
                {isEdited && <span className="msg-edited"> (edited)</span>}
              </p>

              {Array.isArray(message.attachments) &&
                message.attachments.length > 0 && (
                  <div className="msg-attachments">
                    <Attachment attachments={message.attachments} />
                  </div>
                )}
            </>
          )}
        </div>

        {!isMutedByMe && !mine && (
          <div style={{ "--avatar-color": avatarColor }}>
            <Avatar
              image={message.user?.image}
              name={message.user?.name || message.user?.id}
              size={36}
              data-user={message.user?.id}
              className="custom-avatar"
            />
          </div>
        )}
      </div>

      {!isDeleted && !isMutedByMe && (
        <div className={`msg-actions-row ${mine ? "mine" : "theirs"}`}>
          <ReactionsList />
          <MessageOptions
            handleOpenThread={() => {
              try {
                handleInlineReplyAsQuote();
                try {
                  if (channel) {
                    channel.lastReadLocal = new Date().toISOString();
                    channel.markRead?.();
                  }
                } catch (e2) {}
              } catch (e) {}
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomMessage;
